import type {
	AuthArtifact,
	ConnectorAuthMethod,
	ConnectorAuthSchema,
	ConnectorAutomationEvent,
	ConnectorBrowserRequirement,
	FeedOperation,
} from "@lobu/connector-sdk";
import type {
	ConnectionConnectInput,
	InstallConnectorInput,
} from "@lobu/core/contracts/tools/manage-connections";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	ATTENTION_INBOX_QUERY_ROOTS,
	invalidateAttentionInbox,
} from "./attention-cache";
import {
	CONTENT_QUERY_ROOT,
	PUBLIC_CONTENT_QUERY_ROOT,
} from "./content-query-keys";
import {
	API_URL,
	type ApiOrgContext,
	apiCall,
	apiErrorFor,
	fetchWithTimeout,
	resolveApiScope,
	tryResolveApiScopeSlug,
} from "./core";
import {
	createMutation,
	createQuery,
	MAX_TOOL_PAGE_SIZE,
} from "./hook-factory";

// ============================================================
// Connections, Automations, Contents, Templates - Global Data Views
// ============================================================

// ============================================================
// Connector Types (for source discovery)
// ============================================================

export interface ConnectorTypeField {
	name: string;
	type: "string" | "number" | "boolean" | "select" | "array";
	required: boolean;
	description?: string;
	default?: unknown;
	options?: Array<{ value: string; label: string }>;
}

export type { ConnectorAuthMethod, ConnectorAuthSchema };

export interface ConnectorType {
	type: string;
	name: string;
	description?: string;
	icon?: string;
	fields: ConnectorTypeField[];
	options_schema?: Record<string, unknown>;
	auth_schema?: ConnectorAuthSchema;
}

// =============================================================================
// V1 Integration Platform — Connections, Feeds, Runs
// =============================================================================

/**
 * Capability facets used for connections, connector groups, and client-side
 * connector-list projections. Stage 2b.
 * - `data` — ingests data into memory (feeds → events).
 * - `chat` — a live bot adapter the agent converses through.
 * - `actions` — exposes operations the agent can invoke.
 * - `audience` — read access gated by the source's own membership
 *   (Slack channel members, GitHub repo collaborators).
 * - `inference` — serves model inference. Client-only: no `connections` row
 *   ever carries it, because model providers live in their own
 *   `inference_providers` table and are synthesized into the connectors list as
 *   their own rows. The server never derives it, so every projection of a
 *   server response sets it to false explicitly (see `deriveConnectorFacets`)
 *   rather than leaving it optional and silently undefined.
 * - `runtime` — remote sandbox execution. Client-only for the same reason:
 *   sandboxes live in their own table and are synthesized into the list
 *   (`sandbox-rows.ts`).
 */
export interface ConnectionFacets {
	data: boolean;
	chat: boolean;
	actions: boolean;
	audience: boolean;
	inference: boolean;
	runtime: boolean;
}

export interface ConnectionItem {
	id: number;
	slug: string;
	organization_id: string;
	connector_key: string;
	display_name: string | null;
	/** Provider-side tenant this connection is scoped to (a Slack workspace, a
	 * Teams tenant, a GitHub org). Every connection is conceptually tenanted;
	 * the column is populated where routing needs it and NULL elsewhere. */
	external_tenant_id: string | null;
	status: string;
	config: Record<string, unknown> | null;
	error_message: string | null;
	created_by: string | null;
	created_by_username: string | null;
	created_at: string;
	updated_at: string;
	connector_name: string | null;
	auth_profile_slug: string | null;
	auth_profile_name: string | null;
	auth_profile_status: string | null;
	auth_profile_kind:
		| "env"
		| "oauth_app"
		| "oauth_account"
		| "browser_session"
		| "interactive"
		| null;
	app_auth_profile_slug: string | null;
	app_auth_profile_name: string | null;
	app_auth_profile_status: string | null;
	app_auth_profile_kind:
		| "env"
		| "oauth_app"
		| "oauth_account"
		| "browser_session"
		| "interactive"
		| null;
	/**
	 * Live event count. Present on the per-connection detail response
	 * (`handleGet`) but intentionally omitted from list responses
	 * (`handleList`) because the per-row supersedes anti-join was the
	 * dominant cost (1303ms → 2.3ms once dropped). For aggregate displays,
	 * use the events sparkline series instead of summing this field.
	 */
	event_count?: number;
	feed_count: number;
	connect_token: string | null;
	/** Entities this connection is directly tagged with (connections.entity_ids). */
	entity_ids: number[] | null;
	/**
	 * UNION of the connection's own entity_ids and any of its feeds' entity_ids,
	 * resolved to entity names (comma-separated). A connection counts under an
	 * entity if it is directly tagged OR one of its feeds is.
	 */
	entity_names: string | null;
	visibility: "org" | "private";
	/** If set, this connection runs on a specific device worker instead of the Lobu server (serverless). */
	device_worker_id: string | null;
	device_label: string | null;
	device_platform: string | null;
	device_worker_handle: string | null;
	device_online: boolean | null;
	device_last_seen_at: string | null;
	/** 'offline' when pinned to a device that hasn't checked in recently — runs wait for it. */
	device_status: "offline" | null;
	operations_summary?: OperationSummary;
	has_operations?: boolean;
	/** Derived capability facets — what this connection does (Stage 2b). */
	facets: ConnectionFacets;
	/**
	 * `managed` = Lobu-hosted OAuth install ("Add to Slack" / GitHub App);
	 * `byo` = you supplied the token/credentials; `null` = neither resolved.
	 */
	effective_credential_mode: "managed" | "byo" | null;
}

export interface OperationSummary {
	total: number;
	reads: number;
	writes: number;
	local_action: number;
	mcp_tool: number;
	http_operation: number;
}

export interface AuthProfileItem {
	id: number;
	organization_id: string;
	slug: string;
	display_name: string;
	connector_key: string;
	profile_kind: "env" | "oauth_app" | "oauth_account" | "browser_session";
	status: "active" | "pending_auth" | "error" | "revoked";
	provider: string | null;
	created_by: string | null;
	created_at: string;
	updated_at: string;
	requested_scopes?: string[];
	granted_scopes?: string[];
	connect_url?: string;
	cookie_count?: number;
	captured_at?: string | null;
	auth_cookie_name?: string | null;
	expires_at?: string | null;
	is_expired?: boolean;
	has_auth_data?: boolean;
	auth_mode?: "cookies" | "empty" | "live";
	/** Device worker that scopes this browser session profile. */
	device_worker_id?: string | null;
	/** Browser binary on the device when device_worker_id is set. */
	browser_kind?: "chrome" | "brave" | "arc" | "edge" | null;
	/** True when this oauth_app profile is the org default for its connector. */
	is_default_for_connector?: boolean;
}

export interface FeedItem {
	id: number;
	connection_id: number;
	display_name: string | null;
	feed_key: string;
	status: string;
	/** Connector operations implemented by this feed, as declared by the SDK. */
	operations: FeedOperation[];
	/** Platform storage lens. Connector data defaults to events; chat channels
	 * use channel_messages and render as conversations. */
	store: "events" | "channel_messages";
	config: Record<string, unknown> | null;
	entity_ids: number[] | null;
	entity_names?: string | null;
	connector_key: string;
	connector_name: string | null;
	connection_name: string | null;
	connection_status: string | null;
	/** Slack workspace/team id (`connections.external_tenant_id`) for managed
	 * installs; null for BYO / non-Slack. Needed to bind a streaming channel with
	 * the right team scope. */
	external_tenant_id: string | null;
	/** For a streaming (chat-channel) feed: the agent its channel is currently
	 * bound to, or null if unbound. Lets the Automations Listen picker avoid
	 * silently stealing a channel already owned by another agent. */
	target_agent_id?: string | null;
	auth_profile_kind:
		| "env"
		| "oauth_app"
		| "oauth_account"
		| "browser_session"
		| "interactive"
		| null;
	auth_profile_status: string | null;
	active_runs: number;
	event_count: number;
	last_sync_at: string | null;
	last_sync_status: string | null;
	last_error: string | null;
	consecutive_failures: number;
	next_run_at: string | null;
	execution_mode?: "source_only" | "streaming" | "scheduled" | "no_schedule";
	attention?:
		| "healthy"
		| "paused"
		| "needs_auth"
		| "setup_required"
		| "last_attempt_failed"
		| "never_run"
		| "no_trigger"
		| "device_offline"
		| "misconfigured"
		| "overdue";
	attention_reason?: string;
	created_at: string;
	updated_at: string;
	first_failure_at: string | null;
	// Device-worker binding of the parent connection (for "waiting on device" state).
	device_worker_id: string | null;
	device_label: string | null;
	device_platform: string | null;
	device_last_seen_at: string | null;
	device_online: boolean | null;
	device_status: "offline" | null;
}

export interface ConnectorRuntimeInfo {
	platforms: Array<"ios" | "android" | "macos" | "windows" | "linux">;
	scopes?: string[];
}

export interface ConnectorDefinitionItem {
	browser?: ConnectorBrowserRequirement | null;
	key: string;
	name: string;
	description: string | null;
	icon?: string | null;
	favicon_domain?: string | null;
	version: string;
	auth_schema: Record<string, unknown> | null;
	feeds_schema: Record<string, unknown> | null;
	actions_schema: Record<string, unknown> | null;
	automation_events?: ConnectorAutomationEvent[];
	options_schema: Record<string, unknown> | null;
	default_connection_config?: Record<string, unknown> | null;
	status: string;
	login_enabled: boolean;
	source_uri?: string | null;
	installed?: boolean;
	installable?: boolean;
	catalog_origin?: "org" | "catalog";
	operations_summary?: OperationSummary;
	has_operations?: boolean;
	required_capability?: string | null;
	/** Present only for device-bound connectors. Absence means server-side. */
	runtime?: ConnectorRuntimeInfo | null;
}

type InstalledItemWire = {
	id: string;
	name: string;
	detail?: Record<string, unknown>;
};

function mapInstalledConnectorItem(
	item: InstalledItemWire,
): ConnectorDefinitionItem {
	const detail = item.detail ?? {};
	return {
		key: item.id,
		name: item.name,
		description:
			typeof detail.description === "string" ? detail.description : null,
		version: typeof detail.version === "string" ? detail.version : "0.0.0",
		browser: (detail.browser as ConnectorBrowserRequirement | null) ?? null,
		auth_schema:
			detail.auth_schema && typeof detail.auth_schema === "object"
				? (detail.auth_schema as Record<string, unknown>)
				: null,
		feeds_schema:
			detail.feeds_schema && typeof detail.feeds_schema === "object"
				? (detail.feeds_schema as Record<string, unknown>)
				: null,
		actions_schema:
			detail.actions_schema && typeof detail.actions_schema === "object"
				? (detail.actions_schema as Record<string, unknown>)
				: null,
		automation_events: Array.isArray(detail.automation_events)
			? (detail.automation_events as ConnectorAutomationEvent[])
			: [],
		options_schema:
			detail.options_schema && typeof detail.options_schema === "object"
				? (detail.options_schema as Record<string, unknown>)
				: null,
		default_connection_config:
			detail.default_connection_config &&
			typeof detail.default_connection_config === "object"
				? (detail.default_connection_config as Record<string, unknown>)
				: null,
		status: typeof detail.status === "string" ? detail.status : "active",
		login_enabled: Boolean(detail.login_enabled),
		icon: typeof detail.icon === "string" ? detail.icon : null,
		favicon_domain:
			typeof detail.favicon_domain === "string" ? detail.favicon_domain : null,
		required_capability:
			typeof detail.required_capability === "string"
				? detail.required_capability
				: null,
		runtime:
			detail.runtime && typeof detail.runtime === "object"
				? (detail.runtime as ConnectorRuntimeInfo)
				: null,
		source_uri:
			typeof detail.source_uri === "string" ? detail.source_uri : null,
		installed: detail.installed !== false,
		installable: Boolean(detail.installable ?? detail.installed === false),
		catalog_origin:
			detail.catalog_origin === "catalog" || detail.catalog_origin === "org"
				? detail.catalog_origin
				: detail.installed === false
					? "catalog"
					: "org",
		operations_summary: detail.operations_summary as
			| OperationSummary
			| undefined,
		has_operations: Boolean(detail.has_operations),
	};
}

interface AvailableOperationItem {
	connector_key: string;
	connector_name: string;
	operation_key: string;
	name: string;
	description?: string;
	kind: "read" | "write";
	backend: "local_action" | "mcp_tool" | "http_operation";
	policy?: {
		effect: "auto" | "approval" | "deny";
		ruleIds: number[];
		reason: string;
	};
	input_schema?: Record<string, unknown>;
	output_schema?: Record<string, unknown>;
	readiness?: string;
	required_scopes?: string[];
	next_action?: { requested_scopes?: string[]; view_url?: string };
}

export function usePublicConnectorDefinitions(
	orgSlug?: string | null,
	options?: { entityId?: number },
) {
	const entityId = options?.entityId;
	return useQuery({
		queryKey: ["public-connector-definitions", orgSlug, entityId],
		queryFn: async () => {
			const scope = resolveApiScope({ slug: orgSlug ?? null });
			const query = entityId ? `?entity_id=${entityId}` : "";
			const response = await fetchWithTimeout(
				`${API_URL}/api/${scope.slug}/public/connectors${query}`,
				{
					credentials: "include",
				},
			);
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			const result = (await response.json()) as {
				installed?: { connectors?: { items?: InstalledItemWire[] } };
			};
			const items = result.installed?.connectors?.items ?? [];
			return items.map(mapInstalledConnectorItem);
		},
		enabled: !!orgSlug,
		staleTime: 60000,
	});
}

// ============================================================
// Query hooks
// ============================================================

export interface ConnectionGroupSummary {
	id: number;
	display_name: string | null;
	feed_count: number;
}

export interface ConnectionConnectorGroup {
	connector_key: string;
	connector_name: string | null;
	favicon_domain: string | null;
	connection_count: number;
	connections: ConnectionGroupSummary[];
	/**
	 * Connector-level capability facets — the union across the group's members
	 * (chat = any member is a chat connection, data = any feeds or the connector
	 * declares feeds, actions = connector has operations, audience = connector in
	 * the ACL registry). Stage 2b.
	 */
	facets: ConnectionFacets;
}

export const useConnectionConnectorGroups = createQuery<
	[organizationId?: string | null, options?: { entityId?: number }],
	ConnectionConnectorGroup[]
>({
	queryKey: (organizationId, options) => [
		"connections",
		"groups",
		organizationId,
		options?.entityId,
	],
	tool: "manage_connections",
	body: (_orgId, options) => ({
		action: "list_connector_groups",
		entity_id: options?.entityId,
	}),
	transform: (r) => r.groups ?? [],
	enabled: (organizationId) => !!organizationId,
	staleTime: 60000,
});

interface ConnectionListOptions {
	connectorKey?: string;
	status?: string;
	entityId?: number;
	createdBy?: string;
	connectionIds?: number[];
	limit?: number;
	enabled?: boolean;
}

function connectionQueryOptions(
	organizationId?: string | null,
	options?: ConnectionListOptions,
) {
	return {
		queryKey: [
			"connections",
			organizationId,
			options?.connectorKey,
			options?.status,
			options?.entityId,
			options?.createdBy,
			options?.connectionIds,
			options?.limit,
		],
		queryFn: async (): Promise<ConnectionItem[]> => {
			// Keep later pages in the same workspace if navigation changes mid-fetch.
			const scope = resolveApiScope();
			const connections: ConnectionItem[] = [];
			// Explicit limits retain their meaning for bounded previews. Inventory
			// callers load all pages of this bounded configuration collection.
			const limit = Math.min(options?.limit ?? 100, MAX_TOOL_PAGE_SIZE);
			let offset = 0;
			while (true) {
				const result = await apiCall<{
					connections?: ConnectionItem[];
					total?: number;
				}>(
					"manage_connections",
					{
						action: "list",
						connector_key: options?.connectorKey,
						status: options?.status,
						entity_id: options?.entityId,
						created_by: options?.createdBy,
						connection_ids: options?.connectionIds,
						limit,
						offset,
					},
					scope,
				);
				const page = result.connections ?? [];
				connections.push(...page);
				offset += page.length;
				if (
					options?.limit !== undefined ||
					page.length < limit ||
					offset >= (result.total ?? Infinity)
				)
					return connections;
			}
		},
		staleTime: 60000,
	};
}

export const useConnections = Object.assign(
	(organizationId?: string | null, options?: ConnectionListOptions) =>
		useQuery({
			...connectionQueryOptions(organizationId, options),
			enabled: !!organizationId && (options?.enabled ?? true),
		}),
	{ queryOptions: connectionQueryOptions },
);

export const useConnectorDefinitions = createQuery<
	[organizationId?: string | null],
	ConnectorDefinitionItem[]
>({
	queryKey: (organizationId) => ["connector-definitions", organizationId],
	tool: "manage_catalog",
	body: () => ({
		action: "list_installed",
		kinds: ["connectors"],
		include_catalog: true,
	}),
	transform: (r) => {
		const items = r.installed?.connectors?.items as
			| InstalledItemWire[]
			| undefined;
		return (items ?? [])
			.filter((item) => item.detail?.installed !== false)
			.map(mapInstalledConnectorItem);
	},
	enabled: (organizationId) => !!organizationId,
	staleTime: 60000,
});

export async function fetchInstallableConnectorCatalog(
	orgContext?: string | ApiOrgContext,
 options?: { signal?: AbortSignal },
): Promise<ConnectorDefinitionItem[]> {
	const result = await apiCall<{
		installed?: { connectors?: { items?: InstalledItemWire[] } };
	}>(
		"manage_catalog",
		{
			action: "list_installed",
			kinds: ["connectors"],
			include_catalog: true,
		},
		orgContext,
 options,
	);
	return (result.installed?.connectors?.items ?? []).map(
		mapInstalledConnectorItem,
	);
}

export function useInstallableConnectorCatalog(organizationId?: string | null) {
	return useQuery({
		queryKey: ["connector-definitions", "catalog", organizationId],
		queryFn: () => fetchInstallableConnectorCatalog(),
		enabled: !!organizationId,
		staleTime: 60000,
	});
}

useInstallableConnectorCatalog.queryOptions = (
	organizationId?: string | null,
) => ({
	queryKey: ["connector-definitions", "catalog", organizationId],
	queryFn: () => fetchInstallableConnectorCatalog(),
	enabled: !!organizationId,
	staleTime: 60000,
});

export const useAuthProfiles = createQuery<
	[
		organizationId?: string | null,
		options?: {
			connectorKey?: string;
			provider?: string;
			profileKind?: AuthProfileItem["profile_kind"];
		},
	],
	AuthProfileItem[]
>({
	queryKey: (organizationId, options) => [
		"auth-profiles",
		organizationId,
		options?.connectorKey,
		options?.provider,
		options?.profileKind,
	],
	tool: "manage_auth_profiles",
	body: (_orgId, options) => ({
		action: "list_auth_profiles",
		connector_key: options?.connectorKey,
		provider: options?.provider,
		profile_kind: options?.profileKind,
	}),
	transform: (r) => r.auth_profiles ?? [],
	enabled: (organizationId) => !!organizationId,
	// SSE pushes `{ keys: ['auth-profiles'] }` on changes (worker-api.ts:1316),
	// so we don't need to refetch on every mount.
	staleTime: 60000,
});

export const useFeeds = createQuery<[connectionId?: number | null], FeedItem[]>(
	{
		queryKey: (connectionId) => ["feeds", connectionId],
		tool: "manage_feeds",
		body: (connectionId) => ({
			action: "list_feeds",
			connection_id: connectionId,
		}),
		transform: (r) => r.feeds ?? [],
		enabled: (connectionId) => !!connectionId,
	},
);

interface FeedRunItem {
	id: number;
	status: string;
	items_collected: number | null;
	error_message: string | null;
	created_at: string;
	completed_at: string | null;
	checkpoint: Record<string, unknown> | null;
	connector_version: string | null;
}

export const useFeedDetail = createQuery<
	[feedId?: number | null],
	{ feed: FeedItem; recent_runs: FeedRunItem[] }
>({
	queryKey: (feedId) => ["feed-detail", feedId],
	tool: "manage_feeds",
	body: (feedId) => ({ action: "read_feed", feed_id: feedId }),
	enabled: (feedId) => !!feedId,
});

export const useAllFeeds = createQuery<
	[
		organizationId?: string | null,
		options?: {
			entityId?: number;
			status?: string;
			feedIds?: number[];
			enabled?: boolean;
		},
	],
	FeedItem[]
>({
	queryKey: (organizationId, options) => [
		"feeds",
		"all",
		organizationId,
		options?.entityId,
		options?.status,
		options?.feedIds,
	],
	tool: "manage_feeds",
	body: (_orgId, options) => ({
		action: "list_feeds",
		entity_id: options?.entityId,
		status: options?.status,
		feed_ids: options?.feedIds,
	}),
	transform: (r) => r.feeds ?? [],
	enabled: (organizationId, options) =>
		!!organizationId && (options?.enabled ?? true),
});

// ============================================================
// Mutation hooks
// ============================================================

export const useCreateConnection = createMutation<
	{
		connector_key: string;
		display_name?: string;
		auth_profile_slug?: string | null;
		app_auth_profile_slug?: string | null;
		config?: Record<string, unknown>;
		created_by?: string;
		/** Entity IDs to tag this connection with (links the connection to entities). */
		entity_ids?: number[];
		/** Run this connection on a specific device worker (its device_workers.id). Required for connectors with a required_capability. */
		device_worker_id?: string | null;
	},
	{
		action: "create";
		status?: "setup_required";
		setup_family?: string;
		instructions?: string;
		connection_id?: number;
		connection: ConnectionItem;
		connector: ConnectorDefinitionItem;
		view_url?: string;
		auth_run_id?: number;
	}
>({
	tool: "manage_connections",
	body: (p) => ({ action: "create", ...p }),
	invalidateKeys: ["devices", "connections"],
});

export interface ExecuteActionResult {
	run_id: number;
	status: "completed" | "failed" | "pending_approval";
	output?: Record<string, unknown>;
	error_message?: string;
	message?: string;
	metadata?: Record<string, unknown>;
}

export const useExecuteAction = createMutation<
	{
		connection_id: number;
		operation_key: string;
		input: Record<string, unknown>;
	},
	ExecuteActionResult
>({
	tool: "manage_operations",
	body: (p) => ({
		action: "execute",
		connection_id: p.connection_id,
		operation_key: p.operation_key,
		input: p.input,
	}),
	invalidateKeys: ["connections", "runs"],
});

export const useAvailableOperations = createQuery<
	[connectionId: number | null | undefined],
	AvailableOperationItem[]
>({
	queryKey: (connectionId) => ["available-operations", connectionId],
	tool: "manage_operations",
	body: (connectionId) => ({
		action: "list_available",
		connection_id: connectionId,
		include_input_schema: true,
		include_output_schema: true,
		limit: MAX_TOOL_PAGE_SIZE,
		offset: 0,
	}),
	transform: (result) => result.operations ?? [],
	enabled: (connectionId) => !!connectionId,
	staleTime: 30000,
});

export const useConnectorOperations = createQuery<
	[connectorKey: string | null | undefined],
	AvailableOperationItem[]
>({
	queryKey: (connectorKey) => ["connector-operations", connectorKey],
	tool: "manage_operations",
	body: (connectorKey) => ({
		action: "list_available",
		connector_key: connectorKey,
		include_input_schema: false,
		include_output_schema: false,
		limit: MAX_TOOL_PAGE_SIZE,
		offset: 0,
	}),
	transform: (result) => result.operations ?? [],
	enabled: (connectorKey) => !!connectorKey,
	staleTime: 30000,
});

export const useUpdateConnection = createMutation<{
	connection_id: number;
	display_name?: string;
	status?: string;
	auth_profile_slug?: string | null;
	app_auth_profile_slug?: string | null;
	config?: Record<string, unknown>;
	/** Entity IDs to tag this connection with. Pass [] to clear all links; omit to leave unchanged. */
	entity_ids?: number[];
	/** Reassign which device worker runs this connection; null moves it back to the Lobu server (serverless). */
	device_worker_id?: string | null;
	/** Share with the workspace (`org`) or keep private. Only the creator may change it. */
	visibility?: ConnectionItem["visibility"];
}>({
	tool: "manage_connections",
	body: (p) => ({ action: "update", ...p }),
	invalidateKeys: ["devices", "connections"],
});

export const useCreateAuthProfile = createMutation<
	{
		connector_key: string;
		profile_kind: AuthProfileItem["profile_kind"];
		display_name: string;
		slug?: string;
		credentials?: Record<string, string>;
		auth_data?: Record<string, unknown>;
		requested_scopes?: string[];
		app_auth_profile_slug?: string;
	},
	{
		auth_profile?: AuthProfileItem;
		pending_slug?: string;
		connect_url?: string;
		connect_token?: string;
	}
>({
	tool: "manage_auth_profiles",
	body: (p) => ({ action: "create_auth_profile", ...p }),
	invalidateKeys: ["auth-profiles", "connector-definitions"],
});

export const useReconnectAuthProfile = createMutation<
	{
		auth_profile_slug: string;
		requested_scopes?: string[];
		app_auth_profile_slug?: string;
	},
	{ auth_profile: AuthProfileItem; connect_url?: string }
>({
	tool: "manage_auth_profiles",
	body: (params) => ({
		action: "update_auth_profile",
		...params,
		reconnect: true,
	}),
	invalidateKeys: ["auth-profiles"],
});

export const useDeleteAuthProfile = createMutation<string>({
	tool: "manage_auth_profiles",
	body: (slug) => ({ action: "delete_auth_profile", auth_profile_slug: slug }),
	invalidateKeys: ["auth-profiles", "connections"],
	successMessage: "Auth profile deleted",
});

export const useSetDefaultAuthProfile = createMutation<
	{ connector_key: string; auth_profile_slug: string | null },
	{ connector_key: string; auth_profile: AuthProfileItem | null }
>({
	tool: "manage_auth_profiles",
	body: (p) => ({ action: "set_default_auth_profile", ...p }),
	invalidateKeys: ["auth-profiles", "connections"],
});

export const useReauthenticateConnection = createMutation<
	{ connection_id: number; requested_scopes?: string[] },
	{
		action: "reauthenticate";
		connection_id: number;
		auth_run_id?: number;
		connect_url?: string;
	}
>({
	tool: "manage_connections",
	body: (input) => ({ action: "reauthenticate", ...input }),
	invalidateKeys: ["connections"],
});

export const useConnectConnection = createMutation<
	ConnectionConnectInput,
	{
		connection_id?: number;
		connect_url?: string;
		setup_url?: string;
		instructions?: string;
		status?: string;
	}
>({
	tool: "manage_connections",
	body: (input) => ({ action: "connect", ...input }),
	invalidateKeys: ["connections", "auth-profiles", "connector-definitions"],
});

export const useDeleteConnection = createMutation<number>({
	tool: "manage_connections",
	body: (id) => ({ action: "delete", connection_id: id }),
	invalidateKeys: [
		"devices",
		"connections",
		"feeds",
		...ATTENTION_INBOX_QUERY_ROOTS,
	],
	checkError: false,
	successMessage: "Connection deleted",
});

export const useCreateFeed = createMutation<{
	connection_id: number;
	feed_key: string;
	display_name?: string;
	config?: Record<string, unknown>;
	entity_ids?: number[];
}>({
	tool: "manage_feeds",
	body: (p) => ({ action: "create_feed", ...p }),
	invalidateKeys: ["feeds"],
	successMessage: "Feed created",
});

export const useUpdateFeed = createMutation<{
	feed_id: number;
	display_name?: string;
	config?: Record<string, unknown>;
	entity_ids?: number[];
	status?: string;
}>({
	tool: "manage_feeds",
	body: (p) => ({ action: "update_feed", ...p }),
	invalidateKeys: ["feeds"],
});

export const useTriggerFeed = createMutation<number>({
	tool: "manage_feeds",
	body: (feedId) => ({ action: "trigger_feed", feed_id: feedId }),
	invalidateKeys: ["feeds"],
});

// ============================================
// Action Approval
// ============================================

export const useApproveRun = createMutation<
	{ run_id: number; input?: Record<string, unknown> },
	{ approved: true; run_id: number; event_id?: number; message: string }
>({
	tool: "manage_operations",
	body: (p) => ({ action: "approve", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		CONTENT_QUERY_ROOT,
		PUBLIC_CONTENT_QUERY_ROOT,
	],
	invalidateOnError: true,
	successMessage: "Operation approved — executing",
});

export const useRejectRun = createMutation<
	{ run_id: number; reason?: string },
	{ rejected: true; run_id: number; event_id?: number }
>({
	tool: "manage_operations",
	body: (p) => ({ action: "reject", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		CONTENT_QUERY_ROOT,
		PUBLIC_CONTENT_QUERY_ROOT,
	],
	invalidateOnError: true,
	successMessage: "Operation rejected",
});

/**
 * Decide the exact inline-approvable runs currently shown in the attention UI.
 * Each run still passes through the authoritative single-run server gate. The
 * client batches only the presentation and reports partial failure explicitly;
 * it never widens the reviewed set to every pending run in the workspace.
 */
export function useBulkRunDecision() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			decision,
			runIds,
			reason,
		}: {
			decision: "approve" | "reject";
			runIds: number[];
			reason?: string;
		}) => {
			const ids = [...new Set(runIds)];
			const results = await Promise.allSettled(
				ids.map(async (runId) => {
					const result = await apiCall<Record<string, unknown>>(
						"manage_operations",
						decision === "approve"
							? { action: "approve", run_id: runId }
							: {
									action: "reject",
									run_id: runId,
									...(reason ? { reason } : {}),
								},
					);
					if (typeof result.error === "string" && result.error) {
						throw new Error(result.error);
					}
					return runId;
				}),
			);
			const failedRunIds = results.flatMap((result, index) => {
				const id = ids[index];
				return result.status === "rejected" && id !== undefined ? [id] : [];
			});
			return {
				succeeded: ids.length - failedRunIds.length,
				failed: failedRunIds.length,
				failedRunIds,
			};
		},
		onSettled: () => {
			invalidateAttentionInbox(queryClient);
			void queryClient.invalidateQueries({ queryKey: [CONTENT_QUERY_ROOT] });
		},
	});
}

// Approve/reject every pending proposal an automation run produced — the
// one-click batch action on that run's change-set card.
export const useApproveBatch = createMutation<
	{ run_id: number },
	{ approved_count: number; failed_count: number; message: string }
>({
	tool: "manage_operations",
	body: (p) => ({ action: "approve_batch", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		CONTENT_QUERY_ROOT,
		PUBLIC_CONTENT_QUERY_ROOT,
	],
	invalidateOnError: true,
	successMessage: "Proposals approved",
});

export const useRejectBatch = createMutation<
	{ run_id: number; reason?: string },
	{ rejected_count: number; message: string }
>({
	tool: "manage_operations",
	body: (p) => ({ action: "reject_batch", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		CONTENT_QUERY_ROOT,
		PUBLIC_CONTENT_QUERY_ROOT,
	],
	invalidateOnError: true,
	successMessage: "Proposals rejected — ask the agent to revise",
});

export const useInstallConnector = createMutation<InstallConnectorInput>({
	tool: "manage_connections",
	body: (p) => ({ action: "install_connector", ...p }),
	invalidateKeys: ["connector-definitions"],
});

/** Archive an org-installed connector definition (blocked while connections remain). */
export const useUninstallConnector = createMutation<{ connector_key: string }>({
	tool: "manage_connections",
	body: (p) => ({ action: "uninstall_connector", ...p }),
	invalidateKeys: ["connector-definitions", "connections", "feeds"],
	successMessage: "Connector removed",
});

// ============================================================
// Auth Runs (interactive connector.authenticate() lifecycle)
// ============================================================

interface AuthRunStatus {
	id: number;
	status:
		| "pending"
		| "claimed"
		| "running"
		| "completed"
		| "failed"
		| "cancelled";
	connector_key: string | null;
	artifact: AuthArtifact | null;
	error_message: string | null;
	created_at: string;
	completed_at: string | null;
	auth_profile: {
		id: number;
		slug: string | null;
		status: string | null;
	} | null;
}

export function useActiveAuthRun(connectionId: number | null | undefined) {
	return useQuery({
		queryKey: ["auth-run", "active", connectionId],
		queryFn: async () => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/auth-runs/active?connection_id=${connectionId}`,
				{ credentials: "include" },
			);
			if (!response.ok) {
				throw new Error(`Active auth run lookup failed: ${response.status}`);
			}
			return (await response.json()) as { run_id: number | null };
		},
		enabled: !!connectionId,
		staleTime: 0,
	});
}

export function useAuthRun(runId: number | null | undefined) {
	return useQuery({
		queryKey: ["auth-run", runId],
		queryFn: async () => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/auth-runs/${runId}`,
				{
					credentials: "include",
				},
			);
			if (!response.ok) {
				throw new Error(`Auth run fetch failed: ${response.status}`);
			}
			return (await response.json()) as AuthRunStatus;
		},
		enabled: !!runId,
		refetchInterval: (query) => {
			const data = query.state.data as AuthRunStatus | undefined;
			if (!data) return 1000;
			const done = ["completed", "failed", "cancelled"].includes(data.status);
			return done ? false : 1000;
		},
	});
}

export function useSendAuthSignal() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (params: {
			run_id: number;
			name: string;
			payload?: Record<string, unknown>;
		}) => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/auth-runs/${params.run_id}/signal`,
				{
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						name: params.name,
						payload: params.payload ?? {},
					}),
				},
			);
			if (!response.ok) {
				const body = (await response.json().catch(() => ({}))) as {
					error?: string;
				};
				throw new Error(body.error ?? `Signal failed: ${response.status}`);
			}
			return (await response.json()) as { success: true };
		},
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({ queryKey: ["auth-run", vars.run_id] });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to send signal");
		},
	});
}

// ============================================================
// Device Workers
// ============================================================

export interface DeviceWorkerItem {
	/** Surrogate id; pass this as a connection's device_worker_id to pin it here. */
	id: string;
	worker_id: string;
	platform: string | null;
	app_version: string | null;
	capabilities: string[];
	/** Local chat/Automation CLIs this device currently advertises. */
	agent_kinds?: string[] | null;
	label: string | null;
	last_seen_at: string;
	online: boolean;
	/** Workspace this device is attached to (its connectors live there). */
	organization_id: string | null;
	organization_name: string | null;
	organization_slug: string | null;
	/** How many connections are currently pinned to this device. */
	connector_count: number;
	/** Of those, how many are in an error state. */
	connector_error_count: number;
	/** Most recent successful/attempted feed sync across this device's connections. */
	last_sync_at: string | null;
	/** Connections pinned to this device — for cross-linking back into the connection. */
	connectors: Array<{
		connection_id: number;
		connector_key: string;
		display_name: string;
		status: string;
		organization_slug: string | null;
	}>;
	/**
	 * Connectors this device runs at an older version than its team workspace
	 * has installed: it cannot take that version's work, and gets no new
	 * connection for it, until Lobu on the device is updated.
	 */
	update_required: Array<{
		connector_key: string;
		display_name: string;
		device_version: string;
		workspace_version: string;
	}>;
}

const devicesQueryOptions = {
	queryKey: ["devices"] as const,
	queryFn: async (): Promise<DeviceWorkerItem[]> => {
		const response = await fetchWithTimeout(`${API_URL}/api/me/devices`, {
			credentials: "include",
		});
		if (!response.ok) {
			throw new Error(`Failed to fetch devices: ${response.status}`);
		}
		const result = (await response.json()) as { devices?: DeviceWorkerItem[] };
		return result.devices ?? [];
	},
	staleTime: 30000,
};

export function useDevices() {
	return useQuery({
		...devicesQueryOptions,
		refetchInterval: 60000,
	});
}

useDevices.queryOptions = () => devicesQueryOptions;

/**
 * Rename one of your devices (PATCH /api/me/devices/:id { label }).
 * Empty string clears the custom name so the UI falls back to the platform
 * label ("Chrome", "Mac", …).
 */
export function useRenameDevice() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			deviceWorkerId,
			label,
		}: {
			deviceWorkerId: string;
			label: string | null;
		}) => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/me/devices/${encodeURIComponent(deviceWorkerId)}`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ label }),
				},
			);
			if (!response.ok) {
				const body = (await response.json().catch(() => ({}))) as {
					error?: string;
				};
				throw new Error(body.error ?? `Request failed: ${response.status}`);
			}
			return (await response.json()) as { ok: true; label?: string | null };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["devices"] });
			queryClient.invalidateQueries({ queryKey: ["connections"] });
			toast.success("Device renamed");
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to rename device");
		},
	});
}

/**
 * Attach one of your devices to another workspace
 * (PATCH /api/me/devices/:id { organization_id }). The server unpins and
 * pauses every connection pinned to the device, in every workspace, so callers
 * must confirm that with the user first. Errors are left to the caller.
 */
export function useMoveDevice() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			deviceWorkerId,
			organizationId,
		}: {
			deviceWorkerId: string;
			organizationId: string;
		}) => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/me/devices/${encodeURIComponent(deviceWorkerId)}`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ organization_id: organizationId }),
				},
			);
			if (!response.ok) {
				const body = (await response.json().catch(() => ({}))) as {
					error?: string;
				};
				throw new Error(body.error ?? `Request failed: ${response.status}`);
			}
			return (await response.json()) as { ok: true };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["devices"] });
			queryClient.invalidateQueries({ queryKey: ["connections"] });
		},
	});
}

/**
 * Permanently forget one of your devices. Connections pinned to it are
 * un-pinned and paused across every workspace. If the device app is still
 * running it re-registers as a fresh device on its next check-in.
 */
export function useDeleteDevice() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (deviceWorkerId: string) => {
			const response = await fetchWithTimeout(
				`${API_URL}/api/me/devices/${encodeURIComponent(deviceWorkerId)}`,
				{ method: "DELETE", credentials: "include" },
			);
			if (!response.ok) {
				const body = (await response.json().catch(() => ({}))) as {
					error?: string;
				};
				throw new Error(body.error ?? `Request failed: ${response.status}`);
			}
			return (await response.json()) as { ok: true };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["devices"] });
			queryClient.invalidateQueries({ queryKey: ["connections"] });
			toast.success("Device removed");
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to remove device");
		},
	});
}

export const useConnectionSetupOptions = createQuery<
	[organizationId: string, connectorKey: string],
	import("@lobu/core/contracts/tools/manage-connections").ConnectionSetupOptions
>({
	queryKey: (organizationId, connectorKey) => [
		"connections",
		"setup-options",
		tryResolveApiScopeSlug(),
		organizationId,
		connectorKey,
	],
	tool: "manage_connections",
	body: (_organizationId, connectorKey) => ({
		action: "setup_options",
		connector_key: connectorKey,
	}),
	enabled: (organizationId, connectorKey) => !!organizationId && !!connectorKey,
	staleTime: 30000,
});
